import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (e2) {
      // The endpoint always returns 200, so only network errors hit here.
      setErr(e2.response?.data?.detail || "Could not reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 admin-grid-bg p-4">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <div className="font-display text-4xl font-black text-white tracking-tight">
            IL<span className="text-amber-500">/</span>NEWSROOM
          </div>
        </div>

        {done ? (
          <div
            data-testid="forgot-password-success-card"
            className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="text-emerald-500" size={22} />
              <div className="font-display text-lg font-bold text-white">Check your inbox</div>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              If an account with that email exists, we&apos;ve sent a reset link valid for 15 minutes. The link is single-use.
            </p>
            <p className="text-xs text-zinc-500 font-mono">
              Operational copy is BCC&apos;d to the monitored mailbox.
            </p>
            <Link
              data-testid="forgot-password-back-to-login"
              to="/admin/login"
              className="block w-full text-center bg-white text-zinc-950 hover:bg-zinc-200 transition-colors py-2.5 rounded-sm text-sm font-semibold"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form
            data-testid="forgot-password-form"
            onSubmit={submit}
            className="border border-zinc-800 bg-zinc-900/50 backdrop-blur p-6 rounded-sm space-y-4"
          >
            <div>
              <h1 className="font-display text-xl font-bold text-white mb-1">Reset your passphrase</h1>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Enter the operator email and we&apos;ll send a recovery link.
              </p>
            </div>
            <div>
              <label className="label-tiny block mb-1.5">Operator Email</label>
              <input
                data-testid="forgot-password-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="admin-input w-full bg-transparent border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600"
                placeholder="you@intentlogic.com"
              />
            </div>

            {err && (
              <div
                data-testid="forgot-password-error"
                className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono"
              >
                {err}
              </div>
            )}

            <button
              data-testid="forgot-password-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 transition-colors py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="animate-spin" size={14} />}
              Send recovery link
            </button>

            <div className="pt-2 border-t border-zinc-800 text-center">
              <Link
                data-testid="forgot-password-cancel-link"
                to="/admin/login"
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors font-mono"
              >
                ← Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
